import { useEffect, useState } from "react";
import { ClipboardList, Plus, Trash2 } from "lucide-react";
import { Button, Field, IconButton, Pill, Segmented, TextInput, TH, TD, TD_NUM } from "../../../../components/ui";
import { fmtCurrency } from "../../../../utils/format";
import NoDataState from "../../../../../components/NoDataState";
import SectionShell from "../SectionShell";
import useSectionDraft from "../useSectionDraft";
import { CellNumber, CellText, InlineSelect } from "../cells";

const BASIS = ["Lump", "Each", "Day", "Week", "Month"].map((b) => ({ value: b, label: b }));
const QTY_HINT = { Lump: "1", Each: "default qty", Day: "× project days", Week: "× project weeks", Month: "× project months" };

export default function GcTemplatesSection({ config, readOnly, onSaved }) {
  const draft = useSectionDraft(config, "gcTemplates", onSaved, "GC templates");
  const templates = draft.value;
  const [activeId, setActiveId] = useState(config.defaultGcTemplateId);

  // Keep a valid selection when templates are added / reset.
  useEffect(() => {
    if (templates && !templates.some((t) => t.id === activeId)) setActiveId(templates[0]?.id);
  }, [templates, activeId]);

  if (!templates) return null;
  const ti = Math.max(0, templates.findIndex((t) => t.id === activeId));
  const tpl = templates[ti];

  const setItem = (i, key) => (v) => draft.update((d) => { d[ti].items[i][key] = v; });
  const addItem = () =>
    draft.update((d) => {
      d[ti].items.push({ id: `gc-item-${Date.now().toString(36)}`, item: "New item", basis: "Lump", rate: 0 });
    });
  const addTemplate = () => {
    const id = `gc-${Date.now().toString(36)}`;
    draft.update((d) => {
      d.push({ id, name: "New template", items: [] });
    });
    setActiveId(id);
  };

  return (
    <SectionShell
      title="GC Templates"
      subtitle="General-conditions line items. Day / week / month items scale with project duration."
      icon={ClipboardList}
      draft={draft}
      readOnly={readOnly}
      actions={!readOnly && <Button size="sm" icon={Plus} onClick={addTemplate}>New template</Button>}
    >
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Segmented
          ariaLabel="GC template"
          value={tpl?.id}
          onChange={setActiveId}
          options={templates.map((t) => ({ value: t.id, label: t.name, count: t.items.length }))}
        />
        {tpl && (
          <Field label="Template name" htmlFor="gc-template-name" className="w-64">
            <TextInput
              id="gc-template-name"
              value={tpl.name}
              disabled={readOnly}
              onChange={(e) => draft.update((d) => { d[ti].name = e.target.value; })}
            />
          </Field>
        )}
        {tpl?.id === config.defaultGcTemplateId && <Pill tone="primary">Default for new estimates</Pill>}
      </div>

      {!tpl || tpl.items.length === 0 ? (
        <NoDataState
          icon="package"
          title="No items in this template"
          message="Add the general-conditions items this template should include."
          showBack={false}
          showRefresh={false}
          showCreate={!readOnly}
          createText="Add item"
          onCreate={addItem}
          size="small"
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-[4px] border border-border">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={TH}>Item</th>
                  <th className={TH}>Basis</th>
                  <th className={`${TH} text-right`}>Rate</th>
                  <th className={`${TH} text-right`}>Default qty</th>
                  <th className={TH}>Qty rule</th>
                  <th className={`${TH} text-right`}>Cost @ 30 days</th>
                  {!readOnly && <th className={`${TH} w-12`} aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                {tpl.items.map((it, i) => {
                  const days = 30;
                  const qty = { Day: days, Week: Math.ceil(days / 5), Month: Math.ceil(days / 22), Each: it.defaultQty || 1, Lump: 1 }[it.basis] || 1;
                  return (
                    <tr key={it.id}>
                      <td className={`${TD} min-w-[240px] py-1`}>
                        <CellText value={it.item} onChange={setItem(i, "item")} disabled={readOnly} ariaLabel="GC item name" />
                      </td>
                      <td className={`${TD} py-1`}>
                        <InlineSelect value={it.basis} options={BASIS} onChange={setItem(i, "basis")} disabled={readOnly} ariaLabel={`${it.item} basis`} minWidth={100} />
                      </td>
                      <td className={`${TD} w-32 py-1`}>
                        <CellNumber value={it.rate} onChange={setItem(i, "rate")} disabled={readOnly} step="5" min={0} suffix="$" ariaLabel={`${it.item} rate`} />
                      </td>
                      <td className={`${TD} w-28 py-1`}>
                        {it.basis === "Each" ? (
                          <CellNumber value={it.defaultQty ?? 1} onChange={setItem(i, "defaultQty")} disabled={readOnly} step="1" min={0} ariaLabel={`${it.item} default qty`} />
                        ) : (
                          <span className="block text-right text-text_muted">auto</span>
                        )}
                      </td>
                      <td className={`${TD} text-[12px] text-text_secondary`}>{QTY_HINT[it.basis]}</td>
                      <td className={TD_NUM}>{fmtCurrency(qty * (Number(it.rate) || 0))}</td>
                      {!readOnly && (
                        <td className={`${TD} py-1`}>
                          <IconButton icon={Trash2} size="xs" variant="ghost" label={`Remove ${it.item}`} onClick={() => draft.update((d) => { d[ti].items.splice(i, 1); })} />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!readOnly && (
            <div className="mt-2">
              <Button size="xs" icon={Plus} onClick={addItem}>
                Add item
              </Button>
            </div>
          )}
        </>
      )}
    </SectionShell>
  );
}
